import type { DraftLock } from './collabTypes';

export type LockState =
  | { lost: false }
  | {
      lost: true;
      reason: 'taken_over' | 'unlocked' | 'in_review';
      owner: string | null;
      at: string | null;
      /** After an unlock, someone (maybe the unlocking admin) checked out a fresh copy. */
      nowHeldBy?: string | null;
    };

/**
 * Does the person on the edit page still hold the working copy they opened?
 *
 * `openedVersionId` is the copy the page loaded; `probe` is the latest answer from the cheap
 * lock endpoint — `undefined` until it has answered once, `null` when the server says no copy is
 * open any more. A lost lock is one of: someone took over (a different copy, not mine), an admin
 * force-unlocked / it was discarded (no copy), or the same copy was submitted elsewhere.
 */
export function lockState(openedVersionId: number, probe: DraftLock | null | undefined): LockState {
  if (probe === undefined) return { lost: false };
  if (probe === null) return { lost: true, reason: 'unlocked', owner: null, at: null };
  if (probe.version_id !== openedVersionId || !probe.is_mine) {
    return { lost: true, reason: 'taken_over', owner: probe.owner, at: probe.checked_out_at };
  }
  if (probe.state !== 'draft') {
    return { lost: true, reason: 'in_review', owner: probe.owner, at: probe.checked_out_at };
  }
  return { lost: false };
}

/**
 * Keep the FIRST reason the lock was lost. The probe only sees states, not events: after an admin
 * force-unlocks and then checks out a fresh copy, the probe shows "a different copy, not mine",
 * which read as a take-over. The person was unlocked; the later check-out is extra information,
 * not a new reason. A lock that comes back (they checked out again) clears the loss.
 */
export function rememberLoss(prev: LockState, next: LockState): LockState {
  if (!next.lost) return next;
  if (!prev.lost) return next;
  if (prev.reason === 'unlocked' && next.reason === 'taken_over') {
    return { ...prev, nowHeldBy: next.owner };
  }
  return prev;
}

/** One string per lock state so a watcher can notice a change without comparing fields. */
export function lockSignature(probe: DraftLock | null | undefined): string {
  if (probe === undefined) return 'pending';
  if (probe === null) return 'none';
  return `${probe.version_id}:${probe.state}:${probe.is_mine ? 'mine' : 'theirs'}`;
}

/** The edit page must show a lost lock within seconds; the document page only needs to stop
 *  offering a stale "Check out to edit" reasonably soon. Measured (2026-09-05, 2 uvicorn workers,
 *  Postgres): 500 pages at 5s ≈ 96 probes/s, p99 46 ms, 1 active DB connection; 2000 pages at 5s
 *  (~400/s) saturated the workers' CPU — the DB stayed idle, the 3s pool-wait guard answered 503s.
 *  Most open pages are document pages, so 15s there cuts the fleet-wide rate by ~3×.
 *
 *  10s, not 5s (2026-09-15). This is the single biggest source of steady load in the vault: it
 *  is the fastest interval on the busiest screen, so it sets the ceiling on how many people can
 *  have an editor open at once. `loadtest/harnesses/vault_collab_polls.py` measured the whole
 *  collaboration poll mix on the same 2 workers production runs: healthy to ~900 open screens
 *  (177 req/s), tail latency into seconds by 1200. Halving this rate moves that ceiling by about
 *  half again, for free.
 *
 *  What the extra 5s costs: the window in which someone keeps typing into a copy that is no
 *  longer theirs. It is bounded and already handled — every save against a lost copy is refused
 *  by the server, and the page swaps the editor for LockLostNotice as soon as the probe answers.
 *  Losing a checkout mid-edit is a rare, deliberate act by an admin or a colleague, not a race,
 *  so paying for it with a poll five times a minute on every open editor was the wrong trade. */
export const EDIT_PAGE_PROBE_MS = 10_000;
/** How often an open review page re-reads the round: the decisions and the discussion. Slower
 *  than the lock probe because nothing here is a race — it is two people talking, and a message
 *  landing within half a minute reads as live. Both polled queries back off on failure.
 *
 *  30s while the discussion is SHUT (2026-09-15): nobody is watching the conversation, so the
 *  only thing this interval decides is how stale the decision list is for a reviewer who is
 *  reading the document. DISCUSSION_POLL_MS below is what keeps a live conversation live, and
 *  it is deliberately unchanged. */
export const REVIEW_POLL_MS = 30_000;

/** …and faster while the discussion is actually open on screen: at that moment the two people
 *  are talking to each other, and a message that takes 20s to appear reads as broken. */
export const DISCUSSION_POLL_MS = 8_000;

export const DOC_PAGE_PROBE_MS = 15_000;

/** …and how fast the REVIEW page watches while it is waiting for a submit (2026-10-09).
 *
 *  This is the one screen where somebody is actively staring at a page waiting for it to change:
 *  a colleague has said "I'm sending it over", and at 15s they wait up to fifteen seconds in
 *  front of a page that already went stale. It reads as broken, and the reviewer reloads — which
 *  is exactly the habit the watcher exists to remove.
 *
 *  Affordable because of WHEN it runs, not how cheap it is. The watcher stops the moment the
 *  copy is in review (the page's own polling takes over), so the fast rate is paid only during
 *  the short window someone is waiting — and `refetchIntervalInBackground: false` means a parked
 *  tab pays nothing at all. Reviewers are a small share of open screens; editors, who are the
 *  fleet's real load, are untouched at EDIT_PAGE_PROBE_MS.
 *
 *  Revisit if reviewers ever sit on waiting review pages in the hundreds: the measured mix
 *  (loadtest/harnesses/vault_collab_polls.py, 2 workers) is healthy to ~900 open screens, and
 *  this interval is 7.5× the document page's rate per page that uses it. */
export const REVIEW_WAIT_PROBE_MS = 2_000;

/** …and how long that fast rate lasts before falling back to DOC_PAGE_PROBE_MS.
 *
 *  The speed is for a person WATCHING the screen, because a colleague said "I'm sending it
 *  over". A review page left open and visible on a second monitor is not that person, and at a
 *  flat 2s it would ask forever — the only poll in the vault with no natural end, and the one
 *  thing here whose cost grows with nothing but elapsed time. Twenty such tabs is 10 req/s with
 *  nobody present.
 *
 *  Two minutes because that is about as long as anyone waits in front of a page before giving
 *  up on it; past that they are not watching, whatever the tab is doing. A submit after the
 *  settle still arrives within DOC_PAGE_PROBE_MS, which is where this page was yesterday — the
 *  fallback is the old behaviour, not a degradation of it. */
export const REVIEW_WAIT_SETTLE_MS = 120_000;
const PROBE_BACKOFF_CAP_MS = 60_000;

/** Next probe delay: the page's base interval, doubled per consecutive failure (a saturated
 *  server answering 503 must not be hammered by the very pages waiting on it), capped at 60s. */
export function pollInterval(baseMs: number, consecutiveFailures: number): number {
  return Math.min(PROBE_BACKOFF_CAP_MS, baseMs * 2 ** consecutiveFailures);
}
